import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  ElementRef,
  PLATFORM_ID,
  signal,
  input,
  viewChild,
  inject,
  computed,
  ApplicationRef,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { first } from 'rxjs';

import { Metafield, Product, Variant } from 'src/app/shop';
import { ShopifyMedia } from 'src/app/shop/models/Media.model';
import Swiper from 'swiper';
import { SwiperContainer } from 'swiper/element';

export interface VariantSlide {
  color: string;
  images: { id: string; key: string; value: string[] };
}

@Component({
  selector: 'app-product-image-slider',
  templateUrl: './product-image-slider.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProductImageSliderComponent implements OnChanges, AfterViewInit {
  private cd = inject(ChangeDetectorRef);
  private platformId = inject(PLATFORM_ID);

  product = input<Product | undefined>();
  collectionHandle = input<string | undefined>();
  hasLink = input<boolean>(true);
  showPagination = input<boolean>(true);
  loaded = input<boolean>(true);
  initOnInteraction = input<boolean>(false);
  showVariantImages = input<boolean>(false);
  selectedVariant = input<Variant | undefined | null>();

  isBrowser = isPlatformBrowser(this.platformId);
  appRef = inject(ApplicationRef);
  hydrated = toSignal(this.appRef.isStable.pipe(first(stable => stable === true)));

  showVideos = input<boolean>(false);
  sizes = input<
    | {
        xs?: number;
        sm?: number;
        md?: number;
        lg?: number;
        xl?: number;
        xxl?: number;
      }
    | null
    | undefined
  >();

  test = signal(false);
  // @Input() activeProductImageId?: string;

  swiper = viewChild<ElementRef<SwiperContainer>>('swiper');
  thumbs = viewChild<ElementRef<SwiperContainer>>('thumbs');

  activeProductImageId = computed(() => {
    return this.selectedVariant() && (this.product()?.variants.length || 0 > 1) ? this.selectedVariant()?.image.id : '';
  });

  thumbsLoaded = false;

  activeSlide = signal(0);
  translating = false;
  indexChanging = false;
  transitioning = signal(false);
  loadingUpdates = false;

  swiperInited = signal(false);

  media = computed<ShopifyMedia[]>(() => {
    // Add video's
    const videoTypes = ['VIDEO']; //'EXTERNALVIDEO'
    const videos = this.product()?.media?.filter(a => videoTypes.includes(a.mediaContentType.toUpperCase())) || [];
    let mappedProductImages: ShopifyMedia[] =
      this.product()?.images?.map(a => ({
        id: a.id,
        mediaContentType: 'MEDIAIMAGE',
        previewImage: { ...a },
      })) || [];

    if (!this.showVariantImages()) {
      const variantImages = this.product()?.variants?.map(a => a.image.id);
      const filterImagesIfThereAreVariants = mappedProductImages.filter(
        a => !variantImages?.includes(a.id) || a.id === this.selectedVariant()?.image.id
      );

      mappedProductImages = filterImagesIfThereAreVariants.length > 0 ? filterImagesIfThereAreVariants : mappedProductImages;
    }

    return [...mappedProductImages, ...videos];
  });

  ngAfterViewInit(): void {
    // this.initSwiper();
    //if (this.activeProductImageId) {
    //  this.slideToImage(this.activeProductImageId || '');
    //}
    // if (this.thumbs()?.nativeElement) {
    //   const swiperInstance = this.swiper()!.nativeElement.swiper as Swiper;
    //   // Now set your options dynamically
    //   swiperInstance.thumbs.swiper = this.thumbs()!.nativeElement.swiper;
    //   // Re-init or update Swiper to apply changes
    //   swiperInstance.update();
    // }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (changes['loaded'] || changes['product'] || changes['selectedVariant']) {
      this.swiper()?.nativeElement?.swiper?.update();
      this.thumbs()?.nativeElement?.swiper?.update();

      if (changes['selectedVariant'] && (this.product()?.variants.length || 0) > 1) {
        setTimeout(() => {
          if (this.activeProductImageId()) {
            this.slideToImage(this.activeProductImageId()!);
          }
        }, 100);
      }
    }
  }

  slideNext() {
    this.swiper()?.nativeElement?.swiper?.slideNext(100);
  }
  slidePrev() {
    this.swiper()?.nativeElement?.swiper?.slidePrev(100);
  }

  goToSlide(i: number = 0) {
    this.swiper()?.nativeElement?.swiper?.slideTo(i);
  }

  indexChange(e: Event) {
    this.activeSlide.set((<CustomEvent>e).detail[0].realIndex || 0);
  }

  slideToImage(imageId: string) {
    const index =
      this.media()
        ?.map(img => img.id)
        .indexOf(imageId) ?? -1;

    if (index >= 0) {
      this.goToSlide(index);
    }
  }

  init(e: any) {
    console.log('init', e);
  }

  get customLabel(): Metafield | undefined | null {
    // Find it in single or multiple metafield lists
    const detailLabel = this.product()?.metafields?.find(a => a?.key === 'custom_label');
    const cardLabel = this.product()?.metafield?.key === 'custom_label' ? this.product()?.metafield : undefined;

    return detailLabel || cardLabel;
  }
}
